import { useState } from "react";
import { Button, Col, Row, Table, Typography, message } from "antd";
import { useTranslation } from "react-i18next";
import {
  useDeleteProductOptionCategoryMutation,
  useGetAllMyProductOptionCategoriesQuery,
} from "../graphql/generated/hooks";
import RobinPage from "../components/core/RobinPage";
import EditDeleteActions from "../components/EditDeleteActions";
import MutateProductOptionsCategoryDrawer from "../components/MutateProductOptionsCategoryDrawer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { ProductOptionCategory } from "../graphql/generated/schemas";

function ProductOptionCategoryList() {
  const { t, i18n } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editModeState, setEditModeState] = useState<{
    isEdit: boolean;
    categoryToEdit?: Partial<ProductOptionCategory>;
  }>({ isEdit: false });

  const {
    data: categoriesData,
    loading: loadingCategories,
    refetch: refetchCategories,
  } = useGetAllMyProductOptionCategoriesQuery();

  const [deleteCategory, { loading: deletingCategory }] =
    useDeleteProductOptionCategoryMutation();

  const closeDrawer = () => {
    setEditModeState({ isEdit: false, categoryToEdit: undefined });
    setDrawerOpen(false);
  };

  const handleEdit = (category: any) => {
    setEditModeState({ isEdit: true, categoryToEdit: category });
    setDrawerOpen(true);
  };

  const dataSource =
    categoriesData?.getAllProductOptionCategoriesForPartner?.map(
      (category, index) => ({
        key: index,
        name: i18n.language === "ar" ? category.nameAr : category.name,
        options:
          category.productOptions
            .map((opt) => (i18n.language === "ar" ? opt.nameAr : opt.name))
            .join(", ") || "",
        min: category.min,
        max: category.max,
        actions: (
          <EditDeleteActions
            onEdit={() => handleEdit(category)}
            onDelete={() => {
              deleteCategory({ variables: { id: category.id } })
                .then(() => {
                  messageApi.success(t("message.success.data_deleted"));
                  refetchCategories();
                })
                .catch((error) => {
                  messageApi.error(t("message.error.data_delete_failed"));
                });
            }}
            loadingDelete={deletingCategory}
          />
        ),
      })
    );

  const columns = [
    {
      title: t("label.name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("label.options"),
      dataIndex: "options",
      key: "options",
    },
    {
      title: t("label.min"),
      dataIndex: "min",
      key: "min",
    },
    {
      title: t("label.max"),
      dataIndex: "max",
      key: "max",
    },
    {
      title: t("label.actions"),
      dataIndex: "actions",
      key: "actions",
    },
  ];

  return (
    <RobinPage
      title={t("label.menu.productOptionCategories")}
      actions={[
        {
          label: (
            <Typography.Link
              onClick={() => {
                refetchCategories();
              }}
            >
              <FontAwesomeIcon icon={faRotateRight} />
            </Typography.Link>
          ),
        },
        {
          label: (
            <Button
              onClick={() => {
                setEditModeState({ isEdit: false, categoryToEdit: undefined });
                setDrawerOpen(true);
              }}
            >
              {t("button.add")}
            </Button>
          ),
        },
      ]}
    >
      {contextHolder}
      <Row gutter={[10, 10]}>
        <Col span={24}>
          <Table
            loading={loadingCategories}
            dataSource={dataSource}
            columns={columns}
          />
        </Col>
      </Row>

      {/* Drawer component */}
      <MutateProductOptionsCategoryDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        editMode={editModeState.isEdit}
        categoryToEdit={editModeState.categoryToEdit}
      />
    </RobinPage>
  );
}
export default ProductOptionCategoryList;
